import axios, { AxiosResponse } from 'axios';
import { ApiResponse } from '@/types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    // Token is handled via httpOnly cookies, no need to add manually
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    return response;
  },
  (error) => {
    // Let React Router handle authentication redirects instead of forcing page reload
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  register: (email: string, password: string, role?: string) =>
    api.post('/auth/register', { email, password, role }),
  
  logout: () =>
    api.post('/auth/logout'),
  
  me: () =>
    api.get('/auth/me'),
  
  refreshToken: () =>
    api.post('/auth/refresh'),
};

// Projects API
export const projectsApi = {
  getAll: () =>
    api.get('/projects'),
  
  create: (name: string) =>
    api.post('/projects', { name }),
  
  getById: (id: number) =>
    api.get(`/projects/${id}`),
  
  update: (id: number, data: any) =>
    api.put(`/projects/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/projects/${id}`),
  
  share: (id: number, userEmail: string, permission: 'read' | 'write') =>
    api.post(`/projects/${id}/share`, { user_email: userEmail, permission }),
  
  removeShare: (id: number, userId: number) =>
    api.delete(`/projects/${id}/share/${userId}`),
  
  getShares: (id: number) =>
    api.get(`/projects/${id}/shares`),
  
  sync: (id: number) =>
    api.post(`/projects/${id}/sync`),
  
  getSize: (id: number) =>
    api.get(`/projects/${id}/size`),
};

// Files API
export const filesApi = {
  getAll: (projectId: number) =>
    api.get(`/files/${projectId}/files`),
  
  getContent: (projectId: number, filePath: string) =>
    api.get(`/files/${projectId}/files/${encodeURIComponent(filePath)}`),
  
  create: (projectId: number, path: string, name: string, content: string) =>
    api.post(`/files/${projectId}/files`, { path, name, content }),
  
  update: (projectId: number, filePath: string, content: string) =>
    api.put(`/files/${projectId}/files/${encodeURIComponent(filePath)}`, { content }),
  
  delete: (projectId: number, filePath: string) =>
    api.delete(`/files/${projectId}/files/${encodeURIComponent(filePath)}`),
  
  rename: (projectId: number, oldPath: string, newPath: string) =>
    api.post(`/files/${projectId}/files/${encodeURIComponent(oldPath)}/rename`, { newPath }),
  
  uploadAsset: (projectId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/files/${projectId}/assets`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Preview API
export const previewApi = {
  get: (projectId: number, file?: string) => {
    const params = file ? `?file=${encodeURIComponent(file)}` : '';
    return api.get(`/preview/${projectId}${params}`);
  },
  
  getAsset: (projectId: number, assetPath: string) =>
    api.get(`/preview/${projectId}/assets/${encodeURIComponent(assetPath)}`),
  
  refresh: (projectId: number) =>
    api.post(`/preview/${projectId}/refresh`),
};

// Health check
export const healthApi = {
  check: () =>
    api.get('/health'),
};

export default api;